import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsClient, fmtMoney, fmtDate, timeAgo } from './api';
import { Modal, Field, inputCls, btnPrimary, btnGhost, ClientPicker, TimelinePanel, TasksPanel } from './shared';

// CRM > Deals: the revenue pipeline that isn't a placement. One object for
// recruitment BD (retainers, terms) AND subscriptions (Alex running
// MarketMapr's own sales here): one-off, monthly (MRR) or annual value
// models. Kanban by team-wide deal stages; won/lost are statuses.
interface DealStage { id: number; name: string; color: string | null; position: number }
interface Deal {
  id: number; client_id: number | null; contact_person_id: number | null;
  client_name: string | null; live_client_name?: string | null; contact_name?: string | null;
  name: string; stage_id: number | null; status: string;
  value_model: 'one_off' | 'monthly' | 'annual'; amount: number | null; currency: string;
  expected_close: string | null; closed_at: string | null; lost_reason: string | null;
  notes: string | null; created_at: string; updated_at: string;
}

const dealValueLabel = (d: Deal) => {
  if (d.amount === null || d.amount === undefined) return '—';
  const base = fmtMoney(d.amount, d.currency);
  if (d.value_model === 'monthly') return `${base}/mo`;
  if (d.value_model === 'annual') return `${base}/yr`;
  return base;
};
const annualized = (d: Deal) => (d.value_model === 'monthly' ? Number(d.amount || 0) * 12 : Number(d.amount || 0));

const quarterStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
};

const AtsDeals: React.FC = () => {
  const { token } = useAuth();
  const [stages, setStages] = useState<DealStage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [clients, setClients] = useState<AtsClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<Deal | null>(null);
  const [closing, setClosing] = useState<{ deal: Deal; as: 'won' | 'lost' } | null>(null);
  const [lostReason, setLostReason] = useState('');
  const [showStageEditor, setShowStageEditor] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [form, setForm] = useState({ name: '', clientId: '' as string | '', valueModel: 'monthly', amount: '', expectedClose: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [flash, setFlash] = useState('');
  const dragId = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    Promise.all([atsFetch(token, '/deals'), atsFetch(token, '/clients')])
      .then(([d, c]) => { setStages(d.stages); setDeals(d.deals); setClients(c.clients); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const open = deals.filter((d) => d.status === 'open');
  const closed = deals.filter((d) => d.status !== 'open');
  const qs = quarterStart();
  const wonThisQuarter = deals.filter((d) => d.status === 'won' && d.closed_at && new Date(d.closed_at) >= qs);
  const mrrWon = deals.filter((d) => d.status === 'won' && d.value_model === 'monthly').reduce((s, d) => s + Number(d.amount || 0), 0);

  const create = async () => {
    if (!token || !form.name.trim()) return;
    setSaving(true);
    try {
      await atsFetch(token, '/deals', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name, clientId: form.clientId ? Number(form.clientId) : null,
          valueModel: form.valueModel, amount: form.amount === '' ? null : Number(form.amount),
          expectedClose: form.expectedClose || null, notes: form.notes || null,
        }),
      });
      setShowCreate(false);
      setForm({ name: '', clientId: '', valueModel: 'monthly', amount: '', expectedClose: '', notes: '' });
      load();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const moveStage = async (dealId: number, stageId: number) => {
    if (!token) return;
    setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, stage_id: stageId } : d)));
    await atsFetch(token, `/deals/${dealId}`, { method: 'PATCH', body: JSON.stringify({ stageId }) }).catch(() => load());
  };

  const close = async () => {
    if (!token || !closing) return;
    try {
      await atsFetch(token, `/deals/${closing.deal.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: closing.as, lostReason: closing.as === 'lost' ? (lostReason || null) : null }),
      });
      setClosing(null); setLostReason('');
      if (selected?.id === closing.deal.id) setSelected(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const stripeSync = async () => {
    if (!token || syncing) return;
    setSyncing(true);
    try {
      const d = await atsFetch(token, '/deals/stripe-sync', { method: 'POST' });
      setFlash(`Stripe synced — ${d.created} new deal${d.created === 1 ? '' : 's'}, ${d.updated} updated${d.churned ? `, ${d.churned} churned` : ''}.`);
      window.setTimeout(() => setFlash(''), 8000);
      load();
    } catch (e: any) { setError(e.message); }
    setSyncing(false);
  };

  const reopen = async (deal: Deal) => {
    if (!token) return;
    await atsFetch(token, `/deals/${deal.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'open' }) }).catch(() => {});
    load();
  };

  if (loading) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;

  return (
    <div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-[11px] font-medium text-gray-500">Open deals</p>
          <p className="text-xl font-semibold text-gray-900 mt-0.5">{open.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-[11px] font-medium text-gray-500">Pipeline (annualised)</p>
          <p className="text-xl font-semibold text-gray-900 mt-0.5">{fmtMoney(open.reduce((s, d) => s + annualized(d), 0))}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-[11px] font-medium text-gray-500">Won this quarter</p>
          <p className="text-xl font-semibold text-emerald-600 mt-0.5">{fmtMoney(wonThisQuarter.reduce((s, d) => s + annualized(d), 0))}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-[11px] font-medium text-gray-500">MRR won (all time)</p>
          <p className="text-xl font-semibold text-emerald-600 mt-0.5">{fmtMoney(mrrWon)}<span className="text-xs font-normal text-gray-400">/mo</span></p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setShowStageEditor(true)} className={btnGhost}>Edit stages</button>
        <span className="flex items-center gap-2">
          <button
            onClick={stripeSync}
            disabled={syncing}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50"
            title="Pull your Stripe subscriptions in as won deals — clients and MRR appear automatically; cancellations mark deals churned"
          >
            {syncing ? 'Syncing Stripe…' : 'Sync Stripe'}
          </button>
          <button onClick={() => setShowCreate(true)} className={btnPrimary}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New deal
          </button>
        </span>
      </div>
      {flash && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4">{flash}</p>}

      {/* Kanban */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 280 }}>
        {stages.map((s) => {
          const stageDeals = open.filter((d) => d.stage_id === s.id);
          const total = stageDeals.reduce((sum, d) => sum + annualized(d), 0);
          return (
            <div
              key={s.id}
              className={`flex-shrink-0 w-64 rounded-xl border ${dragOver === s.id ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-200 bg-gray-50/60'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(s.id); }}
              onDragLeave={() => setDragOver((v) => (v === s.id ? null : v))}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(null);
                if (dragId.current) moveStage(dragId.current, s.id);
                dragId.current = null;
              }}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color || '#9CA3AF' }} />
                <span className="text-xs font-semibold text-gray-700">{s.name}</span>
                <span className="text-[11px] text-gray-400">{stageDeals.length}</span>
                {total > 0 && <span className="ml-auto text-[10px] font-medium text-gray-400">{fmtMoney(total)}</span>}
              </div>
              <div className="px-2 pb-2 space-y-2 min-h-[60px]">
                {stageDeals.map((d) => (
                  <div
                    key={d.id}
                    draggable
                    onDragStart={() => { dragId.current = d.id; }}
                    onClick={() => setSelected(d)}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm px-3 py-2.5 cursor-pointer hover:border-emerald-200 group"
                  >
                    <div className="flex items-start justify-between gap-1">
                      <p className="text-sm font-medium text-gray-900 leading-tight">{d.name}</p>
                      <span className="flex gap-1 opacity-0 group-hover:opacity-100">
                        <button onClick={(e) => { e.stopPropagation(); setClosing({ deal: d, as: 'won' }); }} title="Mark won" className="text-emerald-500 hover:text-emerald-700 text-xs">✓</button>
                        <button onClick={(e) => { e.stopPropagation(); setClosing({ deal: d, as: 'lost' }); }} title="Mark lost" className="text-gray-300 hover:text-red-500 text-xs">✕</button>
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{d.client_name || d.live_client_name || '—'}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs font-semibold text-emerald-700">{dealValueLabel(d)}</span>
                      {d.expected_close && <span className="text-[10px] text-gray-400">{fmtDate(d.expected_close)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Won / Lost — part of the board: drag a deal in to close it */}
        {([['won', 'Won', 'emerald'], ['lost', 'Lost', 'red']] as const).map(([statusKey, label, tone]) => {
          const colDeals = closed.filter((d) => d.status === statusKey).slice(0, 30);
          const total = colDeals.reduce((sum, d) => sum + annualized(d), 0);
          return (
            <div
              key={statusKey}
              className={`flex-shrink-0 w-64 rounded-xl border ${
                dragOver === (statusKey === 'won' ? -1 : -2)
                  ? (tone === 'emerald' ? 'border-emerald-400 bg-emerald-50/60' : 'border-red-300 bg-red-50/60')
                  : (tone === 'emerald' ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-100 bg-red-50/20')
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(statusKey === 'won' ? -1 : -2); }}
              onDragLeave={() => setDragOver((v) => (v === (statusKey === 'won' ? -1 : -2) ? null : v))}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(null);
                const d = deals.find((x) => x.id === dragId.current && x.status === 'open');
                if (d) setClosing({ deal: d, as: statusKey });
                dragId.current = null;
              }}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className={`w-2 h-2 rounded-full ${tone === 'emerald' ? 'bg-emerald-500' : 'bg-red-400'}`} />
                <span className={`text-xs font-semibold ${tone === 'emerald' ? 'text-emerald-700' : 'text-red-500'}`}>{label}</span>
                <span className="text-[11px] text-gray-400">{colDeals.length}</span>
                {statusKey === 'won' && total > 0 && <span className="ml-auto text-[10px] font-medium text-emerald-600">{fmtMoney(total)}</span>}
              </div>
              <div className="px-2 pb-2 space-y-2 min-h-[60px]">
                {colDeals.map((d) => (
                  <div
                    key={d.id}
                    onClick={() => setSelected(d)}
                    className={`bg-white rounded-lg border shadow-sm px-3 py-2.5 cursor-pointer group ${tone === 'emerald' ? 'border-emerald-200' : 'border-gray-200 opacity-80'}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <p className="text-sm font-medium text-gray-900 leading-tight">{d.name}</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); reopen(d); }}
                        title="Reopen — returns to the pipeline at its old stage"
                        className="opacity-0 group-hover:opacity-100 text-[10px] text-blue-600 hover:text-blue-700 flex-shrink-0"
                      >
                        Reopen
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{d.client_name || d.live_client_name || '—'}{d.lost_reason ? ` — ${d.lost_reason}` : ''}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-xs font-semibold ${tone === 'emerald' ? 'text-emerald-700' : 'text-gray-400 line-through'}`}>{dealValueLabel(d)}</span>
                      {d.closed_at && <span className="text-[10px] text-gray-400">{timeAgo(d.closed_at)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Deal drawer */}
      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)} wide>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-4 text-sm">
            <p><span className="text-gray-400 text-xs uppercase font-semibold mr-2">Client</span>
              {selected.client_id ? <Link to={`/ats/clients/${selected.client_id}`} className="text-blue-700 hover:underline">{selected.client_name || selected.live_client_name}</Link> : (selected.client_name || '—')}
            </p>
            <p><span className="text-gray-400 text-xs uppercase font-semibold mr-2">Value</span><span className="font-medium text-emerald-700">{dealValueLabel(selected)}</span>
              {selected.value_model === 'monthly' && selected.amount ? <span className="text-xs text-gray-400"> ({fmtMoney(annualized(selected))} ARR)</span> : null}
            </p>
            <p><span className="text-gray-400 text-xs uppercase font-semibold mr-2">Expected close</span>{fmtDate(selected.expected_close)}</p>
            <p><span className="text-gray-400 text-xs uppercase font-semibold mr-2">Status</span><span className="capitalize">{selected.status}</span>{selected.closed_at ? ` · ${fmtDate(selected.closed_at)}` : ''}</p>
          </div>
          {selected.notes && <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 mb-4 whitespace-pre-wrap">{selected.notes}</p>}
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Activity</p>
              <TimelinePanel entityType="deal" entityId={selected.id} />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Tasks</p>
              <TasksPanel entityType="deal" entityId={selected.id} />
            </div>
          </div>
        </Modal>
      )}

      {/* Stage editor — create the kanban rows you want */}
      {showStageEditor && (
        <Modal title="Pipeline stages" onClose={() => { setShowStageEditor(false); load(); }}>
          <p className="text-xs text-gray-400 mb-3">Rename inline (saves on blur), reorder with the arrows, add your own. Won and Lost are fixed outcomes, not stages.</p>
          <ul className="space-y-2 mb-4">
            {stages.map((s, i) => (
              <li key={s.id} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || '#9CA3AF' }} />
                <input
                  defaultValue={s.name}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (token && v && v !== s.name) {
                      atsFetch(token, `/deal-stages/${s.id}`, { method: 'PATCH', body: JSON.stringify({ name: v }) })
                        .then(() => setStages((prev) => prev.map((x) => x.id === s.id ? { ...x, name: v } : x)))
                        .catch((err: any) => setError(err.message));
                    }
                  }}
                  className={inputCls}
                />
                <button
                  disabled={i === 0}
                  onClick={async () => {
                    if (!token) return;
                    const order = stages.map((x) => x.id);
                    [order[i - 1], order[i]] = [order[i], order[i - 1]];
                    const d = await atsFetch(token, '/deal-stages/reorder', { method: 'POST', body: JSON.stringify({ order }) }).catch(() => null);
                    if (d) setStages(d.stages);
                  }}
                  className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-30"
                >↑</button>
                <button
                  disabled={i === stages.length - 1}
                  onClick={async () => {
                    if (!token) return;
                    const order = stages.map((x) => x.id);
                    [order[i], order[i + 1]] = [order[i + 1], order[i]];
                    const d = await atsFetch(token, '/deal-stages/reorder', { method: 'POST', body: JSON.stringify({ order }) }).catch(() => null);
                    if (d) setStages(d.stages);
                  }}
                  className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-30"
                >↓</button>
                <button
                  onClick={async () => {
                    if (!token) return;
                    try {
                      await atsFetch(token, `/deal-stages/${s.id}`, { method: 'DELETE' });
                      setStages((prev) => prev.filter((x) => x.id !== s.id));
                    } catch (err: any) { setError(err.message); }
                  }}
                  className="p-1 text-gray-300 hover:text-red-500"
                  title="Delete (only when no open deals sit in it)"
                >✕</button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <input
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && token && newStageName.trim()) {
                  const d = await atsFetch(token, '/deal-stages', { method: 'POST', body: JSON.stringify({ name: newStageName.trim() }) }).catch(() => null);
                  if (d) { setStages((prev) => [...prev, d.stage]); setNewStageName(''); }
                }
              }}
              placeholder="New stage name…"
              className={inputCls}
            />
            <button
              onClick={async () => {
                if (!token || !newStageName.trim()) return;
                const d = await atsFetch(token, '/deal-stages', { method: 'POST', body: JSON.stringify({ name: newStageName.trim() }) }).catch(() => null);
                if (d) { setStages((prev) => [...prev, d.stage]); setNewStageName(''); }
              }}
              disabled={!newStageName.trim()}
              className={btnPrimary}
            >
              Add
            </button>
          </div>
          {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
        </Modal>
      )}

      {/* Create deal */}
      {showCreate && (
        <Modal title="New deal" onClose={() => setShowCreate(false)}>
          <div className="space-y-3">
            <Field label="Deal name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme — £449 plan" className={inputCls} autoFocus /></Field>
            <Field label="Client">
              <ClientPicker
                clients={clients}
                valueId={form.clientId ? Number(form.clientId) : null}
                onSelect={(id) => setForm({ ...form, clientId: String(id) })}
                onCreate={async (name) => {
                  if (!token) return null;
                  const d = await atsFetch(token, '/clients', { method: 'POST', body: JSON.stringify({ name }) });
                  setClients((prev) => [d.client, ...prev]);
                  return d.client;
                }}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Value type">
                <select value={form.valueModel} onChange={(e) => setForm({ ...form, valueModel: e.target.value })} className={inputCls}>
                  <option value="monthly">Monthly (MRR)</option>
                  <option value="annual">Annual</option>
                  <option value="one_off">One-off</option>
                </select>
              </Field>
              <Field label={form.valueModel === 'monthly' ? 'Amount / month' : form.valueModel === 'annual' ? 'Amount / year' : 'Amount'}>
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Expected close"><input type="date" value={form.expectedClose} onChange={(e) => setForm({ ...form, expectedClose: e.target.value })} className={inputCls} /></Field>
            <Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} /></Field>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowCreate(false)} className={btnGhost}>Cancel</button>
              <button onClick={create} disabled={saving || !form.name.trim()} className={btnPrimary}>{saving ? 'Creating…' : 'Create deal'}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Win / lose confirm */}
      {closing && (
        <Modal title={closing.as === 'won' ? `Mark won: ${closing.deal.name}` : `Mark lost: ${closing.deal.name}`} onClose={() => setClosing(null)}>
          {closing.as === 'won' ? (
            <p className="text-sm text-gray-600 mb-4">
              Books {dealValueLabel(closing.deal)}{closing.deal.value_model === 'monthly' ? ` (${fmtMoney(annualized(closing.deal))} annualised)` : ''} as won revenue.
            </p>
          ) : (
            <Field label="Lost reason">
              <select value={lostReason} onChange={(e) => setLostReason(e.target.value)} className={inputCls}>
                <option value="">—</option>
                {['Price', 'Went with competitor', 'No budget', 'Bad timing', 'Ghosted', 'Not a fit', 'Other'].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <button onClick={() => setClosing(null)} className={btnGhost}>Cancel</button>
            <button
              onClick={close}
              className={`inline-flex items-center px-3.5 py-2 rounded-lg text-white text-sm font-medium ${closing.as === 'won' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {closing.as === 'won' ? 'Mark won' : 'Mark lost'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AtsDeals;
